import React, { useState } from "react";
import { OrderLineTypes } from "@repo/database";
import { generateRandomString } from "@repo/utils";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useCartManager } from "~/managers/use-cart-manager";
import { useCartStore } from "~/stores/use-cart-store";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  useForm,
} from "../ui/form";
import { Input } from "../ui/input";
import { ModalInput } from "../ui/modal-input";
import { RegisterButton } from "../ui/register-button";
import Decimal from "decimal.js";

export default function DiscountForm({ onClose }: { onClose: () => void }) {
  const { getTotal } = useCartStore();
  const { addToCart } = useCartManager();
  const { t } = useTranslation("common");
  const form = useForm({
    schema: z.object({
      value: z.number(),
    }),
    defaultValues: {
      value: 0,
    },
  });
  const [percentageDiscount, setPercentageDiscount] = useState(0);

  const [newTotal, setNewTotal] = useState(0);

  const handleSubmit = form.handleSubmit((data) => {
    void addToCart({
      ordered: false,
      printed: false,
      type: OrderLineTypes.discount,
      createdAt: new Date(),
      quantity: 1,
      name: "Discount",
      originalQuantity: 1,
      reference: generateRandomString(8),
      value: data.value,
      vatRate: 0,
    });
    onClose();
  });

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-2">
        <FormItem>
          <FormLabel>{t("Discount.total")}</FormLabel>
          <Input value={getTotal()} disabled />
        </FormItem>
        <FormField
          control={form.control}
          name="value"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("Discount.fixed_discount")}</FormLabel>
              <FormControl>
                <ModalInput
                  type="decimal"
                  value={field.value}
                  onChange={(value) => {
                    const newTotal = getTotal() - value;
                    setPercentageDiscount(
                      Math.abs(((newTotal - getTotal()) / getTotal()) * 100),
                    );
                    setNewTotal(newTotal);
                    field.onChange(value);
                  }}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormItem>
          <FormLabel>{t("Discount.percentage_discount")}</FormLabel>
          <ModalInput
            type="number"
            value={percentageDiscount}
            onChange={(value) => {
              setPercentageDiscount(value);
              // Calculate the new total as an integer
              const discountValue = new Decimal(getTotal() * (value / 100)).toDecimalPlaces(2).toNumber();
              setNewTotal(getTotal() - discountValue);
              form.setValue("value", discountValue);
            }}
          />
        </FormItem>
        <FormItem>
          <FormLabel>{t("Discount.new_total")}</FormLabel>
          <ModalInput
            type="decimal"
            value={newTotal}
            onChange={(value) => {
              setNewTotal(value);
              setPercentageDiscount(
                Math.abs(((value - getTotal()) / getTotal()) * 100),
              );
              form.setValue("value", getTotal() - value);
            }}
          />
        </FormItem>
        <RegisterButton type="submit" variant="success">
          {t("Discount.apply")}
        </RegisterButton>
      </form>
    </Form>
  );
}
